import { format, parseISO } from 'date-fns';
import { countableSessions, dayKey, formatClock, ScheduledSession } from '../schedule';
import { ClientTask, ClientWork, DashboardClient } from '../tasks/types';

/** The worst task, plus how many more: "Generate the initial report + 2 more". */
export const inboxChip = (item: ClientWork) =>
  item.tasks.length > 1 ? `${item.tasks[0].title} + ${item.tasks.length - 1} more` : item.tasks[0].title;

/** Clients with work who have no session with this coach that day (a late cancel still keeps them on the card, with their tasks). */
export const notOnSchedule = (work: ClientWork[], sessions: ScheduledSession[]) => {
  const scheduled = new Set(sessions.map((s) => s.clientId));
  return work.filter((item) => !scheduled.has(item.client.clientId));
};

/** "6 sessions today"; who needs what is left to the list and its filter. Late cancels and no-shows don't count. */
export const headline = (sessions: ScheduledSession[], dayWord: 'today' | 'tomorrow') => {
  const count = countableSessions(sessions).length;
  return count === 0 ? `No sessions booked ${dayWord}` : `${count} session${count === 1 ? '' : 's'} ${dayWord}`;
};

/** Booked sessions whose client has something outstanding (collaborations never do: their tasks are the lead coach's). */
export const needingAttention = (sessions: ScheduledSession[]) => sessions.filter((session) => session.countable && session.work !== null);

/** How many clients on the day's schedule need attention; a client seen twice counts once. */
export const attentionCount = (sessions: ScheduledSession[]) => new Set(needingAttention(sessions).map((s) => s.clientId)).size;

/** The coach's next booked session after `day` in the client list, for the quiet-day message. */
export function nextBookedSession(clients: DashboardClient[], coachId: string, after: Date): { name: string; label: string } | null {
  const key = dayKey(after);
  const coach = Number(coachId);
  let best: { client: DashboardClient; date: string; time: { hours: number; minutes: number } | null } | null = null;
  clients.forEach((client) =>
    client.appointments.forEach((a) => {
      if (a.coachId !== coach || a.date <= key) return;
      const sortKey = (x: { date: string; time: { hours: number; minutes: number } | null }) =>
        x.date + (x.time ? String(x.time.hours * 60 + x.time.minutes).padStart(4, '0') : '9999');
      if (!best || sortKey(a) < sortKey(best)) best = { client, date: a.date, time: a.time };
    })
  );
  if (!best) return null;
  const found = best as { client: DashboardClient; date: string; time: { hours: number; minutes: number } | null };
  return { name: found.client.name, label: `${format(parseISO(found.date), 'EEEE d MMM')} at ${formatClock(found.time)}` };
}

/** One line under a session: a single task, or several that share what you do about them. */
export interface TaskRow {
  key: string;
  /** Leading verb of a grouped row ("Book", "Submit"); null for a single task. */
  verb: string | null;
  /** The task's title, or the grouped objects ("metabolic screening, nutrition session"). */
  text: string;
  /** The row's most urgent task: its due date, link target and check-in. */
  lead: ClientTask;
  tasks: ClientTask[];
}

const GROUPS: Partial<Record<ClientTask['kind'], { verb: string; strip: RegExp }>> = {
  // Bookings all happen in Mindbody, so they read as one job.
  session: { verb: 'Book', strip: /^Book (an? )?/ },
  // Assessments all live on the client's Assessments tab, so they share one link.
  assessment: { verb: 'Submit', strip: /^Submit (the )?/ },
};

/**
 * The Today rows for one client's tasks (already most urgent first): bookings
 * and assessments collapse into one row each when there are several, and every
 * row keeps the position of its most urgent task.
 */
export function taskRows(tasks: ClientTask[]): TaskRow[] {
  const byKind = new Map<ClientTask['kind'], ClientTask[]>();
  tasks.forEach((task) => byKind.set(task.kind, [...(byKind.get(task.kind) ?? []), task]));
  const rows: TaskRow[] = [];
  const grouped = new Set<ClientTask['kind']>();
  tasks.forEach((task) => {
    const group = GROUPS[task.kind];
    const same = byKind.get(task.kind) ?? [];
    if (group && same.length > 1) {
      if (grouped.has(task.kind)) return;
      grouped.add(task.kind);
      rows.push({ key: `${task.kind}-group`, verb: group.verb, text: same.map((t) => t.title.replace(group.strip, '')).join(', '), lead: task, tasks: same });
      return;
    }
    rows.push({ key: task.id, verb: null, text: task.title, lead: task, tasks: [task] });
  });
  return rows;
}
